import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileSignature, Send, CheckCircle, Clock, Plus, Upload, Eye, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';
import ReactMarkdown from 'react-markdown';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  signed: 'bg-green-100 text-green-800 border-green-200',
  declined: 'bg-red-100 text-red-800 border-red-200'
};

export default function EngagementLetters() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', service_line: 'individual_tax', content: '' });
  const [uploading, setUploading] = useState(false);
  
  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [variableValues, setVariableValues] = useState({});
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [sending, setSending] = useState(false);
  
  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  useEffect(() => {
    initializeTemplates();
    loadData();
  }, []);

  const initializeTemplates = async () => {
    try {
      await api.post('/api/admin/init-engagement-templates');
    } catch (error) {
      console.log('Templates already initialized');
    }
  };

  const loadData = async () => {
    try {
      const [templatesRes, assignmentsRes, clientsRes] = await Promise.all([
        api.get('/api/engagement-letters-legacy/templates'),
        api.get('/api/engagement-letters-legacy/assignments'),
        clientsAPI.list({ status: 'active' })
      ]);
      
      setTemplates(templatesRes.data);
      setAssignments(assignmentsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load engagement letters');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadForm.name || !uploadForm.content) {
      toast.error('Name and content are required');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', uploadForm.name);
      formData.append('service_line', uploadForm.service_line);
      formData.append('content', uploadForm.content);

      const response = await api.post('/api/engagement-letters-legacy/templates/upload', formData);
      
      toast.success(`Template uploaded! Found ${response.data.variables_found.length} variables`);
      setUploadDialogOpen(false);
      setUploadForm({ name: '', service_line: 'individual_tax', content: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Delete "${template.name}"?`)) return;

    try {
      await api.delete(`/api/engagement-letters-legacy/templates/${template.id}`);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleOpenSend = (template) => {
    setSelectedTemplate(template);
    const defaults = {};
    template.variables.forEach(v => {
      defaults[v] = '';
    });
    defaults['current_date'] = new Date().toLocaleDateString('en-AU');
    setVariableValues(defaults);
    setSendDialogOpen(true);
  };

  const handlePreview = () => {
    let content = selectedTemplate.content_template;
    Object.entries(variableValues).forEach(([key, value]) => {
      content = content.replace(new RegExp(`{{${key}}}`, 'g'), value || `[${key}]`);
    });
    setPreviewContent(content);
    setPreviewDialogOpen(true);
  };

  const handleSend = async () => {
    if (!selectedClientId || !signerEmail) {
      toast.error('Please select client and enter signer email');
      return;
    }

    setSending(true);
    try {
      await api.post('/api/engagement-letters-legacy/send', {
        client_id: selectedClientId,
        template_id: selectedTemplate.id,
        variable_values: variableValues,
        signers: [{ name: signerName || 'Client', email: signerEmail, role: 'client' }]
      });
      
      toast.success('Engagement letter sent for signature');
      setSendDialogOpen(false);
      setSelectedClientId('');
      setSignerEmail('');
      setSignerName('');
      loadData();
    } catch (error) {
      toast.error('Failed to send letter');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-64 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
          Engagement Letters
        </h1>
        <p className="text-muted-foreground mt-1">Manage engagement letter templates and client signatures</p>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="templates">
              <FileSignature className="w-4 h-4 mr-2" />
              Templates ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="assignments">
              <Send className="w-4 h-4 mr-2" />
              Sent Letters ({assignments.length})
            </TabsTrigger>
          </TabsList>
          
          <Button onClick={() => setUploadDialogOpen(true)} className="bg-primary hover:bg-primary/90">
            <Upload className="w-4 h-4 mr-2" />
            Upload Template
          </Button>
        </div>

        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                    {template.name}
                  </CardTitle>
                  <Badge variant="outline" className="capitalize">{template.service_line.replace('_', ' ')}</Badge>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-muted-foreground">{template.variables.length} variables</p>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleOpenSend(template)} 
                      className="flex-1 bg-primary hover:bg-primary/90"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </Button>
                    <Button 
                      onClick={() => handleDeleteTemplate(template)}
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assignments">
          <Card>
            <CardContent className="pt-6">
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileSignature className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No letters sent yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Template</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Signed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium">{assignment.template_name}</TableCell>
                        <TableCell>{assignment.client_name}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[assignment.status]}>
                            {assignment.status === 'signed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{formatAUDate(assignment.sent_at)}</TableCell>
                        <TableCell className="text-sm">
                          {assignment.signed_at ? formatAUDate(assignment.signed_at) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Template Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Upload Engagement Letter Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                placeholder="e.g., Individual Tax Return 2025"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Service Line</Label>
              <Select 
                value={uploadForm.service_line} 
                onValueChange={(value) => setUploadForm({...uploadForm, service_line: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual_tax">Individual Tax</SelectItem>
                  <SelectItem value="company_bas">Company BAS</SelectItem>
                  <SelectItem value="smsf">SMSF</SelectItem>
                  <SelectItem value="payroll">Payroll</SelectItem>
                  <SelectItem value="advisory">Advisory</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Content (Markdown) *</Label>
              <Textarea
                placeholder="Use {{variable_name}} for dynamic values. Example: Dear {{client_name}}, Fee: {{fee_amount}}"
                value={uploadForm.content}
                onChange={(e) => setUploadForm({...uploadForm, content: e.target.value})}
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Variables detected: Use double curly braces around variable names. System will auto-detect all variables.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUploadTemplate} disabled={uploading} className="bg-primary hover:bg-primary/90">
                {uploading ? 'Uploading...' : 'Upload Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Letter Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Client *</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-800 font-medium mb-2">Fill Variables</p>
                <p className="text-xs text-blue-700">
                  Variables found: {selectedTemplate.variables.join(', ')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {selectedTemplate.variables.filter(v => v !== 'current_date').map((variable) => (
                  <div key={variable} className="space-y-2">
                    <Label className="capitalize">{variable.replace('_', ' ')}</Label>
                    <Input
                      placeholder={variable}
                      value={variableValues[variable] || ''}
                      onChange={(e) => setVariableValues({...variableValues, [variable]: e.target.value})}
                    />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Signer Name</Label>
                  <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="John Smith" />
                </div>
                <div className="space-y-2">
                  <Label>Signer Email *</Label>
                  <Input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} required />
                </div>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={handlePreview}>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSendDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSend} disabled={sending} className="bg-primary hover:bg-primary/90">
                    {sending ? 'Sending...' : 'Send for Signature'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Letter Preview</DialogTitle>
          </DialogHeader>
          <div className="prose prose-slate max-w-none p-6 bg-card border-2 rounded">
            <ReactMarkdown>{previewContent}</ReactMarkdown>
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
